import { computedFrom } from 'aurelia-framework';
import * as moment from 'moment';
import * as numeral from 'numeral';
import { FormattingHelper } from '../helpers/formatting-helper';
import { Portfolio } from './portfolio';
import { PaymentHistory } from './payment-history';
import { I18N } from 'aurelia-i18n';
import { Bucket } from './bucket';
import { MfaTwoFactorMethod } from './mfa/mfa-two-factor-method';


export class Customer {
	firstName: string;
	lastName: string;
	accounts: Array<Account>;
	securitySettings: any;
	primaryAddress: any;
	id: any;
	customerKey: number;
	notificationSettings: any;
	documentSettings: CustomerDocumentSettings;
	isTemporaryPassword: any;
	email: any;
	messages: any;
	homePhone: any;
	workPhone: any;
	mobilePhone: any;
	isRegistered: any;
	hasPrimaryPaymentMethod: boolean;
	preferences: any;
	paymentMethods: any;
	portfolios: any;
	preferredLanguageSettings: CustomerPreferredLanguageSettings;
	mfaSettings: CustomerMfaSettings;
 
	constructor(business: any, config?: any) {
		if (config) {
			this.initialize(business, config);
		} else {
			let sessionCustomer = sessionStorage.customer;
			if (sessionCustomer) {
				let parsedCustomer = JSON.parse(sessionCustomer);
				if (parsedCustomer) {
					this.initialize(business, parsedCustomer);
				}
			}
		}
	}

	@computedFrom('firstName', 'lastName')
	get name() {
		let fullName: string = '';
		if (this.firstName) {
			fullName += this.firstName;
		}
		if (this.lastName) {
			if (fullName.length > 0) {
				fullName += ' ';
			}
			fullName += this.lastName;
		}
		return fullName;
	}

	calculateLastActivityOld(paymentList: any) {
		let now = moment();
		this.accounts.forEach(account => {
			account.lastActivity = null;

			paymentList.forEach(payment => {
				let m = moment(payment.paymentDate);
				if (m < now && payment.accountName === account.name && (payment.result === 'Approved' || payment.transactionType === 'Rehab event')) {
					if (account.lastActivity === null) {
						account.lastActivity = payment.paymentDate;
					} else if (moment(account.lastActivity) < m) {
						account.lastActivity = payment.paymentDate;
					}
				}
			})
		});
	}

	calculateLastActivity(paymentList: any, i18n: I18N) {
		let now = moment();
		this.accounts.forEach(account => {
			account.lastActivity = null;
			paymentList.forEach(payment => {
				let m = moment(payment.paymentDate);
				if (payment.accountName === account.name) {
					if (payment.message !== null && payment.message.slice(-1) === '.') {
						payment.message = payment.message.substring(0, payment.message.length - 1);
					}
					var message = i18n.i18next.t('paymentReceivedOn',"Payment received on ") + " ";
					if (payment.result !== "Approved")
						message = i18n.i18next.t('paymentXOn', { defaultValue: "Payment " + payment.result.toLowerCase()  + " on ", result: payment.result.toLowerCase() }) + " ";

					if (account.lastActivity === null || moment(account.lastActivity) < m) {
						account.lastActivity = payment.paymentDate;
						if (payment.message !== null && payment.message.search('Pending first scheduled payment') > -1) m = moment(payment.startDate);
						account.lastActivityMessage = payment.transactionType === 'Rehab event'
							? payment.message + ' on ' + m.format('M/D/YYYY')
							: (message + m.format('M/D/YYYY'));
					}
				}
			});
		});
	}

	updateSecuritySettings(username: any, question1: any, question2: any) {
		if (!this.securitySettings) {
			this.securitySettings = {};
		}

		this.securitySettings.username = username;
		this.securitySettings.question1 = question1;
		this.securitySettings.question2 = question2;
	}

	setZipCode(zip: any) {
		if (!this.primaryAddress) {
			this.primaryAddress = new Address();
		}
		this.primaryAddress.zip = zip;
	}

	static anonymous(business: any, accountId: any, systemId: any, zipCode?: any, bucketKey?: number) {
		return new Customer(business, {
			firstName: 'Anonymous',
			primaryAddress: {
				zip: zipCode
			},
			accounts: [
				{
					id: accountId,
					portfolioName: business.ezpayPortfolioName,
					name: accountId,
					originalBalance: 0,
					balance: Number.MAX_VALUE,
					systemId: systemId,
					bucketKey: bucketKey
				}
			]
		});
	}

	initialize(business: any, config: any) {
		this.id = config.id;
		this.customerKey = config.customerKey;
		this.securitySettings = new CustomerSecuritySettings(config.securitySettings);
		this.notificationSettings = new CustomerNotificationSettings(config.notificationSettings);
		this.documentSettings = new CustomerDocumentSettings(config.documentSettings);
		this.preferredLanguageSettings = new CustomerPreferredLanguageSettings(config);
		this.mfaSettings = new CustomerMfaSettings(config.mfaSettings);

		if (!config.customerAccounts && !config.accounts) {
			this.isTemporaryPassword = true;
		} else {
			this.firstName = config.firstName || "";
			this.lastName = config.lastName || "";
			this.email = config.email || "";

			this.messages = (config.messages || []).map((item) => new Message(item));

			this.accounts = (config.customerAccounts || config.accounts).map((item) => new Account(item));

			if (config.primaryAddress) {
				this.primaryAddress = new Address(config.primaryAddress);
			} else {
				this.primaryAddress = new Address({
					line1: config.addressLine1,
					line2: config.addressLine2,
					city: config.addressCity,
					state: config.addressState,
					zip: config.addressZip
				});
			}

			this.homePhone = config.phone || config.homePhone || "";
			this.workPhone = config.workPhone || "";
			this.mobilePhone = config.mobilePhone || "";

			this.isRegistered = !!config.securitySettings;

			this.hasPrimaryPaymentMethod = config.hasPrimaryPaymentMethod;

			this.preferences = new CustomerPreferences(config.preferences);

			this.paymentMethods = (config.paymentMethods || []).map((item) => new CustomerPaymentMethod(item));

			this.isTemporaryPassword = false;

			this.portfolios = config.portfolios;
			if (business) {
				if (!!config.portfolios && Array.isArray(config.portfolios)) {
					business.addCustomerPortfolios(this.portfolios.map(item => new Portfolio(item)));
				} else {
					business.addCustomerPortfolios();
				}
			}
		}
	}
}

export class Message {
	id: any;
	effectiveDate: any;
	text: any;

	constructor(config: any) {
		this.id = config.id;
		this.effectiveDate = config.effectiveDate;
		this.text = config.text;
	}
}

export class Account {
	status: any;
	state: any;
	stateDescription: any;
	id: string;
	customerAccountKey: number;
	name: string;
	contactName: string;
	referenceId: string;
	displayName: string;
	originalBalance: number;
	balance: number;
	systemId: string;
	portfolioName: string;
	hasExternalPaymentPlans: boolean;
	paymentPlans: any;
	showRehabPaymentTerms: any;
	proposedAmount: any;
	adjustedBalances: any;
	description: any;
	balanceItems: any;
	minPayment: any;
	lastActivity: Date;
	lastActivityMessage: string;
	hasActiveAutopay: boolean;
	bucket: Bucket | null;
	bucketKey: number | null;
	originalLenderName: string;
	repaymentType: string;

	constructor(config: any) {
		this.status = config.status;
		this.state = config.state;
		this.stateDescription = config.stateDescription;
		this.id = config.id || "";
		this.customerAccountKey = config.customerAccountKey;
		this.name = config.name || config.referenceId || config.id || "";
		this.contactName = config.contactName;
		this.referenceId = config.referenceId;
		this.displayName = config.displayName || this.name;
		this.originalBalance = config.originalBalance || 0;
		this.balance = config.balance || 0;
		this.systemId = config.systemId || '0';
		this.portfolioName = config.portfolioName || '';
		this.hasExternalPaymentPlans = config.hasExternalPaymentPlans || false;
		this.paymentPlans = (config.paymentPlans || []).map((item => new PaymentPlan(item)));
		this.showRehabPaymentTerms = config.showRehabPaymentTerms;
		this.proposedAmount = config.proposedAmount;
		this.adjustedBalances = config.adjustedBalances;
		this.description = config.description;
		this.balanceItems = config.balanceItems;
		this.minPayment = config.minPayment;
		this.hasActiveAutopay = config.hasActiveAutopay || false;
		this.bucketKey = config.bucketKey || null;
		this.bucket = config.bucket || null;
		this.originalLenderName = config.originalLenderName || '';
		this.repaymentType = config.repaymentType || '';
	}
}

export class Payment {
	paymentDate: string;
	accountName: string;
	contactName: string;
	paymentAmount: number;
	result: string;
	PNRef: string;
	transactionType: string;
	message: string;
	startDate: string;
	bucketKey: number;

	constructor(item: PaymentHistory) {
		this.paymentDate = item.paymentDate;
		this.accountName = item.accountNumber;
		this.contactName = item.contactName;
		this.paymentAmount = item.paymentAmount;
		this.result = item.paymentResult || "Approved";
		this.PNRef = item.pnRef;
		this.transactionType = item.transactionType;
		this.message = item.message;
		this.startDate = item.startDate;
	}

	static fetchFromSession() {
		let session = sessionStorage.payments; // portal refactor note: with the below commented out, this does nothing?
		// portal refactor note: sessionpayments doesn't appear to be declared/used anywhere else, so commenting this out
		//if (sessionpayments) {
		//    let parsed = JSON.parse(session);
		//    if (parsed) {
		//        return parsed.map(f => new Payment(f));                    
		//    }
		//}            
	}
}

export class PaymentPlan {
	startDate: any;
	nextPaymentDate: any;
	mostRecentPaymentDate: any;
	planEndDate: any;
	totalPayments: any;
	paymentsSoFar: any;
	nextPaymentAmount: any;
	amountRemaining: any;
	paymentsSkipped: boolean;
	id: any;
	key: number;
	status: any;
	payments: any;
	paymentInstrument: any;
	isRecurring: boolean;
	isSingleFuture: boolean;
	isCombinedCustomerAccount: boolean;

	constructor(config: any) {
		this.startDate = config.startDate;
		this.nextPaymentDate = config.nextPaymentDate;
		this.mostRecentPaymentDate = config.mostRecentPaymentDate;
		this.planEndDate = config.planEndDate;
		this.totalPayments = config.totalPayments;
		this.paymentsSoFar = config.paymentsSoFar;
		this.nextPaymentAmount = config.nextPaymentAmount;
		this.amountRemaining = config.amountRemaining;
		this.paymentsSkipped = config.paymentsSkipped;
		this.id = config.id;
		this.key = config.key;
		this.status = config.status;
		this.payments = config.payments;
		this.paymentInstrument = config.paymentInstrument;
		this.isRecurring = config.isRecurring || false;
		this.isSingleFuture = config.isSingleFuture || false;
		this.isCombinedCustomerAccount = config.isCombinedCustomerAccount || false;
	}
}

export class Address {
	line1: string = '';
	line2: string = '';
	city: string = '';
	state: string = '';
	zip: string = '';

	constructor(config?: any) {
		this.line1 = config.line1 || "";
		this.line2 = config.line2 || "";
		this.city = config.city || "";
		this.state = config.state || "";
		this.zip = config.zip || "";
	}

	@computedFrom('city', 'state', 'zip')
	get cityStateZip() {
		let comma = !!this.city && !!this.state ? ', ' : '';
		return `${this.city}${comma}${this.state} ${this.zip}`;
	}
}

export class CustomerPreferences {
	emailWhenDue: boolean;
	emailWhenLate: boolean;
	emailWhenPaymentIsProcessed: boolean;
	emailWhenPaymentFails: boolean;
	phoneCalls: boolean;

	constructor(config: any) {
		if (config) {
			this.emailWhenDue = config.emailWhenDue || false;
			this.emailWhenLate = config.emailWhenLate || false;
			this.emailWhenPaymentIsProcessed = config.emailWhenPaymentIsProcessored || false;
			this.emailWhenPaymentFails = config.emailWhenPaymentFails || false;
			this.phoneCalls = config.phoneCalls || false;
		}
	}
}

export class CustomerNotificationSettings {
	smsIsOptedIn: boolean;
	smsIsValidated: boolean;
	isEmailOptedIn: boolean;

	constructor(config: any) {
		if (!config)
			config = {};
		this.smsIsOptedIn = (config.smsIsOptedIn === null || typeof (config.smsIsOptedIn) === "undefined") ? false : config.smsIsOptedIn;
		this.smsIsValidated = (config.smsIsValidated === null || typeof (config.smsIsValidated) === "undefined") ? false : config.smsIsValidated;
		this.isEmailOptedIn = config.isEmailOptedIn || false;
	}
}

export class CustomerDocumentSettings {
	isPaperOptedIn: boolean;
	isEmailOptedIn: boolean;

	constructor(config: any) {
		config = config || {};
		this.isPaperOptedIn = config.isPaperOptedIn || false;
		this.isEmailOptedIn = config.isEmailOptedIn || false;
	}
}

export class CustomerSecuritySettings {
	username: string;
	question1: string;
	question2: string;
	wantsEmailNotifications: boolean;

	constructor(config: any) {
		if (!config) config = {};
		this.username = config.username || "";
		this.question1 = config.securityQuestion1 || "";
		this.question2 = config.securityQuestion2 || "";
	}
}

export class CustomerPaymentMethod {
	id: any;
	name: string;
	description: string;

	constructor(config: any) {
		this.id = config.id;
		this.name = config.name || "";
		this['type'] = config['type'] || "";
		this.description = config.description || "";
	}
}

export class CustomerPreferredLanguageSettings {
	preferredLanguageCode: string | null;
	declinedToStatePreferredLanguage: boolean | null;
	otherPreferredLanguage: string | null;

	constructor(config: any) {
		this.preferredLanguageCode = config.preferredLanguageCode;
		this.declinedToStatePreferredLanguage = config.declinedToStatePreferredLanguage;
		this.otherPreferredLanguage = config.otherPreferredLanguage;
	}
}

export class CustomerMfaSettings {
	twoFactorEnabled: boolean;
	preferredTwoFactorMethod: MfaTwoFactorMethod;
	twoFactorVerified: boolean;
	email: string;
	phone: string;

	constructor(config: any) {
		if (!config) { config = {}; }
		this.twoFactorEnabled = config.twoFactorEnabled || false;
		this.preferredTwoFactorMethod = (<any>MfaTwoFactorMethod)[config.preferredTwoFactorMethod] || MfaTwoFactorMethod.NotSet;
		this.twoFactorVerified = false;
		this.email = config.email || null;
		this.phone = config.phone || null;
	}
}

export class TemplateEngine {
	account: any;
	business: any;
	paymentInformation: any;

	constructor(config: any) {
		this.account = config.account;
		this.business = config.business;
		this.paymentInformation = config.business.portalSettings.merchantInformation === undefined ? null : config.portalSettings.merchantInformation;
	}

	run(str: any, obj0?: any) {
		if (!str)
			return null;
		var obj = this.buildTemplateKeyValueMap(obj0);
		var temp = "";
		var newStr = "";
		var isParsing = false;
		for (var i = 0, len = str.length; i < len; i++) {
			if (isParsing) {
				if (str[i] === "{") {
					newStr += "{" + temp;
					temp = "";
					continue;
				}
				if (str[i] === "}") {
					if (obj[temp])
						newStr += obj[temp];
					else
						newStr += temp;
					temp = "";
					isParsing = false;
					continue;
				}
				temp += str[i];
			} else {
				if (str[i] === "{") {
					isParsing = true;
				} else {
					newStr += str[i];
				}
			}
		}
		return newStr;
	}

	buildTemplateKeyValueMap(obj0: any) {
		if (!obj0)
			obj0 = {};
		var obj1 = this.buildAccountTemplateKeyValueMap();
		var obj2 = this.buildMerchantTemplateKeyValueMap();
		var obj3 = this.buildPaymentInformationTemplateKeyValueMap();
		var obj = Object.assign({}, obj0, obj1, obj2, obj3);
		return obj;
	}

	buildAccountTemplateKeyValueMap() {
		var obj = {};
		if (this.account) {
			obj = {
				"accountNumber": this.account.name,
				"account": this.account.name,
				"balance": numeral(this.account.balance).format('($0,0.00)'),
				"paidBalance": "$0,0.00",
				"PendingBalance": "$0,0.00"
			}
			if (this.account.adjustedBalances) {
				obj["paidBalance"] = this.account.adjustedBalances.paidBalance;
				obj["PendingBalance"] = this.account.adjustedBalances.pendingBalance
			}
		}
		return obj;
	}

	buildMerchantTemplateKeyValueMap() {
		var obj = {};
		if (this.business) {
			obj = {
				"accountNumberLabel": this.business.portalSettings.accountNumberLabel,
				"merchantName": this.business.name,
				"rehabPhoneNumber": this.business.phone,
				"merchantPhoneNumber": this.business.phone,
			}
		}
		return obj;
	}

	buildPaymentInformationTemplateKeyValueMap() {
		var obj = {};
		if (this.paymentInformation) {
			obj = {
				"date": moment(this.paymentInformation.transactionDate).format('M/D/YYYY'),
				"transactionDate": moment(this.paymentInformation.transactionDate).format('M/D/YYYY'),
				"discount": this.paymentInformation.discount,
				"accountName": this.paymentInformation.name,
				"zipCode": this.paymentInformation.zipCode,
				"zip": this.paymentInformation.zipCode,
				"description": this.paymentInformation.transactionDescription,
				"transactionDescription": this.paymentInformation.transactionDescription,
				"fee": numeral(this.paymentInformation.fee).format('($0,0.00)'),
				"serviceFee": numeral(this.paymentInformation.fee).format('($0,0.00)'),
				"amount": numeral(this.paymentInformation.amount).format('($0,0.00)'),
				"sumOfInstallment": numeral(this.paymentInformation.amount).format('($0,0.00)'),
				"subtotal": numeral(this.paymentInformation.amount).format('($0,0.00)'),
				"total": numeral(this.paymentInformation.amount + this.paymentInformation.fee).format('($0,0.00)'),
				"totalAmount": numeral(this.paymentInformation.amount + this.paymentInformation.fee).format('($0,0.00)'),
				"address1": this.paymentInformation.address1,
			}
			if (this.paymentInformation.installment) {
				obj["numberOfRetries"] = this.paymentInformation.installment.numberOfRetriesForCardDeclines;
				obj["nextPaymentDate"] = this.paymentInformation.installment.nextPaymentDate;
			}
			if (this.paymentInformation.card) {
				obj["cardNumber"] = typeof this.paymentInformation.card.cardNumber !== 'undefined' ? '***' + FormattingHelper.mask(this.paymentInformation.card.cardNumber) : "";
				obj["expDate"] = this.paymentInformation.card.expirationDate;
				obj["paymentMethod"] = this.paymentInformation.card.description;
			}
			if (this.paymentInformation.check) {
				obj["bankAccount"] = typeof this.paymentInformation.check.bankAccount !== 'undefined' ? '***' + FormattingHelper.mask(this.paymentInformation.check.bankAccount.mask) : "",
					obj["transitNumber"] = this.paymentInformation.check.transitNumber;
				obj["checkNumber"] = this.paymentInformation.check.checkNumber;
				obj["paymentMethod"] = this.paymentInformation.check.description;

			}
		}
		return obj;
	}
}
